import { useCallback } from "react"
import styled from "styled-components"
import { Button, Form, FormField, Input, Label } from "~/components"
import { useForm, useTranslate } from "~/ur-react-hooks"
import { useToast, useUser } from "~/utils"
import { useErrorHandling } from "~/utils/error"
import { trpc } from "~/utils/trpc"
import { validatePassword } from "~/utils/validation"

const Layout = styled.div`
  position: relative;
  margin-top: 1rem;
`

const ButtonWrapper = styled.div`
  margin-top: 2rem;
`

export type SettingsChangePasswordProps = {}

const SettingsChangePassword: React.FC<SettingsChangePasswordProps> = ({}) => {
  const translations = useTranslate({
    currentPassword: "settings.current-password",
    newPassword: "settings.new-password",
    repeatNewPassword: "settings.repeat-new-password",
    changePassword: "settings.tabs.change-password",
    passwordChanged: "settings.toasts.password-changed-successfully",
    error: {
      createUser: "login.create-user-error",
      emailTaken: "errors.from-code.email-already-taken",
      wrongUsernameOrPassword: "error.wrong-username-or-password",
      required: ["error.field-is-required", { field: "" }],
      invalidEmail: "errors.invalid-email-address",
      badPasswords: "errors.change-password-weak",
      nonMatchingPasswords: "errors.passwords-do-not-match",
    },
  })

  const me = useUser()

  const {
    formValues,
    formErrors,
    formChangeHandler,
    submitHandler,
    formEdited,
    formValid,
    resetForm,
  } = useForm({
    values: {
      currentPassword: "",
      newPassword: "",
      repeatNewPassword: "",
    },
    validators: {
      newPassword: validatePassword(
        translations.error.badPasswords,
        "newPassword",
        translations.error.nonMatchingPasswords,
      ),
      repeatNewPassword: validatePassword(
        translations.error.nonMatchingPasswords,
        "repeatNewPassword",
        translations.error.nonMatchingPasswords,
      ),
    },
  })

  const { getToastInfo } = useErrorHandling()
  const toast = useToast()

  const { mutate: changePassword } =
    trpc.authentication.changePassword.useMutation({
      onSuccess: () => {
        toast("success", translations.passwordChanged)
        resetForm()
      },
      onError(error) {
        const { type, message } = getToastInfo(
          error.message,
          error.data?.code,
          "error",
        )

        toast(type, message)
      },
    })

  const save = useCallback(() => {
    if (!formEdited || !formValid) {
      return
    }

    changePassword({
      id: me.id,
      oldPassword: formValues.currentPassword,
      newPassword: formValues.newPassword,
    })
  }, [formEdited, formValid, formValues, me, changePassword])

  return (
    <Layout>
      <Form preventDefault onSubmit={submitHandler(save)}>
        <FormField>
          <Label>{translations.currentPassword}</Label>
          <Input
            type="password"
            name="currentPassword"
            error={formErrors.currentPassword}
            width="100%"
            value={formValues.currentPassword}
            onChange={formChangeHandler("currentPassword")}
          />
        </FormField>
        <FormField>
          <Label>{translations.newPassword}</Label>
          <Input
            type="password"
            name="newPassword"
            error={formErrors.newPassword}
            width="100%"
            value={formValues.newPassword}
            onChange={formChangeHandler("newPassword")}
          />
        </FormField>
        <FormField>
          <Label>{translations.repeatNewPassword}</Label>
          <Input
            type="password"
            name="repeatNewPassword"
            error={formErrors.repeatNewPassword}
            width="100%"
            value={formValues.repeatNewPassword}
            onChange={formChangeHandler("repeatNewPassword")}
          />
        </FormField>
        <ButtonWrapper>
          <Button width="100%">{translations.changePassword}</Button>
        </ButtonWrapper>
      </Form>
    </Layout>
  )
}

export default SettingsChangePassword
